/**
 * Support's API, typed at the edge of this package.
 *
 * Two clients, and the split mirrors the backend's for the same reason: the
 * customer's payload has no company name and no owner on it, because who at
 * the distributor holds their claim is not theirs to know — and a field that
 * could carry it is a field that one day will.
 *
 * `is_internal` exists on the message type because the desk's screen renders
 * it differently. On the portal's side the server has already removed those
 * messages, so the flag is always false there; the type does not pretend
 * otherwise, because a second type would drift from this one.
 */

import type { ApiClient } from "@hardauto/core-api";

const BACK_OFFICE = "/api/v1/support";
const PORTAL = "/api/v1/portal/support";

export type TicketStatus = "open" | "in_progress" | "resolved" | "refused";

export type TicketReason = "warranty" | "wrong_reference" | "damaged" | "missing" | "other";

export type Author = "customer" | "staff";

export type NoteServiceCode = "sales" | "logistics" | "collections" | "adv";
export type NotePriority = "normal" | "high" | "urgent";
export type NoteSubject = "client" | "order";

export interface TicketMessage {
  id: string;
  author: Author;
  body: string;
  created_at: string;
  author_name: string;
  /** The desk talking to itself. Never true on a portal payload. */
  is_internal: boolean;
}

export interface TicketAttachment {
  id: string;
  file_id: string;
  filename: string;
  content_type: string;
  size: number;
  uploaded_at: string;
  author: Author;
}

export interface TimelineStep {
  key: string;
  at: string | null;
  /** `done` · `current` · `todo` — the design's own three states. */
  state: string;
}

export interface Ticket {
  id: string;
  reference: string;
  reason: TicketReason;
  status: TicketStatus;
  subject: string;
  description: string;
  order_id: string | null;
  order_reference: string;
  article_reference: string;
  quantity: number;
  site_code: string;
  opened_at: string | null;
  decided_at: string | null;
  decision_reason: string;
  messages: TicketMessage[];
  attachments: TicketAttachment[];
  /**
   * The progress line, derived by the server.
   *
   * Sent rather than computed here because the shape is a rule about the
   * status — a refused claim's last step is a refusal, not a resolution — and
   * two screens deriving it independently is two chances to disagree.
   */
  timeline: TimelineStep[];
}

/**
 * The desk's view of one claim.
 *
 * Carries the customer and who at the distributor holds it, neither of which
 * is on the shared claim type: a customer has no business knowing which
 * colleague has their claim.
 */
export interface TicketDetail {
  ticket: Ticket;
  client_id: string;
  client_code: string;
  client_name: string;
  owner_name: string;
}

export interface TicketRow {
  ticket: Ticket;
  client_id: string;
  client_code: string;
  client_name: string;
  /** Who at the distributor has it. Empty until somebody takes it. */
  owner_name: string;
}

export interface TicketList {
  items: TicketRow[];
  total: number;
  open_count: number;
  next_cursor: string | null;
}

export interface PortalTicketList {
  items: Ticket[];
  total: number;
  open_count: number;
}

export interface Note {
  id: string;
  subject_kind: NoteSubject;
  subject_id: string;
  service: NoteServiceCode;
  priority: NotePriority;
  body: string;
  author_name: string;
  created_at: string | null;
  handled_at: string | null;
}

export interface TicketQuery {
  client_id?: string | undefined;
  status?: TicketStatus | undefined;
  reason?: TicketReason | undefined;
  open_only?: boolean | undefined;
  search?: string | undefined;
  after_id?: string | undefined;
  limit?: number | undefined;
}

export interface NewTicket {
  reason: TicketReason;
  subject: string;
  description: string;
  order_reference?: string;
  article_reference?: string;
  quantity?: number;
  site_code?: string;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

/** The desk's claims, and the notes beside them. */
export class SupportApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  list(options: TicketQuery = {}): Promise<TicketList> {
    return this.#client.request<TicketList>(
      `${BACK_OFFICE}/tickets${query({ ...options, limit: options.limit ?? 50 })}`,
    );
  }

  get(id: string): Promise<TicketDetail> {
    return this.#client.request<TicketDetail>(`${BACK_OFFICE}/tickets/${encodeURIComponent(id)}`);
  }

  open(body: NewTicket & { client_id: string }): Promise<Ticket> {
    return this.#client.request<Ticket>(`${BACK_OFFICE}/tickets`, { method: "POST", body });
  }

  take(id: string): Promise<Ticket> {
    return this.#client.request<Ticket>(`${BACK_OFFICE}/tickets/${encodeURIComponent(id)}/take`, {
      method: "POST",
    });
  }

  reply(id: string, body: string, options: { internal?: boolean } = {}): Promise<TicketMessage> {
    return this.#client.request<TicketMessage>(
      `${BACK_OFFICE}/tickets/${encodeURIComponent(id)}/messages`,
      { method: "POST", body: { body, internal: options.internal ?? false } },
    );
  }

  decide(id: string, status: TicketStatus, reason = ""): Promise<Ticket> {
    return this.#client.request<Ticket>(
      `${BACK_OFFICE}/tickets/${encodeURIComponent(id)}/decision`,
      { method: "POST", body: { status, reason } },
    );
  }

  attach(id: string, file: File): Promise<TicketDetail> {
    const form = new FormData();
    form.set("upload", file);
    return this.#client.upload<TicketDetail>(
      `${BACK_OFFICE}/tickets/${encodeURIComponent(id)}/attachments`,
      form,
    );
  }

  /**
   * An attachment's bytes.
   *
   * `requestBlob`, not a link: the route is behind a bearer token, and an
   * `<a href>` the browser follows carries no Authorization header.
   */
  attachment(id: string, fileId: string): Promise<Blob> {
    return this.#client.requestBlob(
      `${BACK_OFFICE}/tickets/${encodeURIComponent(id)}/attachments/${encodeURIComponent(fileId)}`,
    );
  }

  // --- Notes (§6.12) ----------------------------------------------------

  notes(
    options: {
      subject_kind?: NoteSubject;
      subject_id?: string;
      service?: NoteServiceCode;
      open_only?: boolean;
    } = {},
  ): Promise<{ items: Note[] }> {
    return this.#client.request<{ items: Note[] }>(`${BACK_OFFICE}/notes${query(options)}`);
  }

  addNote(body: {
    subject_kind: NoteSubject;
    subject_id: string;
    service: NoteServiceCode;
    body: string;
    priority?: NotePriority;
  }): Promise<Note> {
    return this.#client.request<Note>(`${BACK_OFFICE}/notes`, { method: "POST", body });
  }

  handleNote(id: string): Promise<Note> {
    return this.#client.request<Note>(`${BACK_OFFICE}/notes/${encodeURIComponent(id)}/handled`, {
      method: "POST",
    });
  }

  deleteNote(id: string): Promise<void> {
    return this.#client.request<void>(`${BACK_OFFICE}/notes/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }
}

/** A customer's own claims. */
export class PortalSupportApi {
  readonly #client: ApiClient;
  readonly #clientCode: string | undefined;

  /**
   * `clientCode` is the only thing a portal caller may influence, and the
   * server matches it against their own links — a code they are not linked to
   * is refused exactly as if they had none. It exists because a broker acting
   * for three companies should not need three accounts.
   */
  constructor(client: ApiClient, clientCode?: string) {
    this.#client = client;
    this.#clientCode = clientCode;
  }

  #query(params: Record<string, string | number | boolean | undefined> = {}): string {
    return query({ ...params, client_code: this.#clientCode });
  }

  list(options: { open_only?: boolean; limit?: number } = {}): Promise<PortalTicketList> {
    return this.#client.request<PortalTicketList>(
      `${PORTAL}/tickets${this.#query({ ...options, limit: options.limit ?? 50 })}`,
    );
  }

  get(id: string): Promise<Ticket> {
    return this.#client.request<Ticket>(
      `${PORTAL}/tickets/${encodeURIComponent(id)}${this.#query()}`,
    );
  }

  open(body: NewTicket): Promise<Ticket> {
    return this.#client.request<Ticket>(`${PORTAL}/tickets${this.#query()}`, {
      method: "POST",
      body,
    });
  }

  /**
   * A reply, which reopens a resolved claim.
   *
   * No `internal` argument, and that is the guarantee rather than an
   * omission: the server's own schema for this route has no such field.
   */
  reply(id: string, body: string): Promise<TicketMessage> {
    return this.#client.request<TicketMessage>(
      `${PORTAL}/tickets/${encodeURIComponent(id)}/messages${this.#query()}`,
      { method: "POST", body: { body } },
    );
  }

  attach(id: string, file: File): Promise<Ticket> {
    const form = new FormData();
    form.set("upload", file);
    return this.#client.upload<Ticket>(
      `${PORTAL}/tickets/${encodeURIComponent(id)}/attachments${this.#query()}`,
      form,
    );
  }

  attachment(id: string, fileId: string): Promise<Blob> {
    return this.#client.requestBlob(
      `${PORTAL}/tickets/${encodeURIComponent(id)}/attachments/${encodeURIComponent(fileId)}${this.#query()}`,
    );
  }
}
